/**
 * Platform abstraction layer for dual runtime support
 * - Web/PWA (default): current behavior unchanged
 * - Native (Capacitor iOS): additive compatibility only
 * 
 * All Capacitor-specific code is dynamically imported to prevent
 * bundling native-only code into web builds.
 */

/**
 * Check if running inside Capacitor native shell
 * Safe implementation that never throws in browsers
 */
export function isNativeApp(): boolean {
  if (typeof window === 'undefined') return false;
  
  // Check for Capacitor presence
  const capacitor = (window as Window & { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  if (!capacitor) return false;
  
  // Use isNativePlatform() if available, otherwise check for Capacitor object
  if (typeof capacitor.isNativePlatform === 'function') {
    try {
      return capacitor.isNativePlatform();
    } catch {
      return false;
    }
  }
  
  // Fallback: check if Capacitor object exists (indicates native shell)
  return !!capacitor;
}

/**
 * Open external URL in appropriate context
 * - Web: opens in new tab
 * - Native: opens in system browser (SFSafariViewController)
 */
export async function openExternalUrl(url: string): Promise<void> {
  if (isNativeApp()) {
    try {
      // Use Function constructor to prevent Vite from statically analyzing the import
      // This allows the module to be optional without causing dev server errors
      const dynamicImport = new Function('specifier', 'return import(specifier)') as (
        specifier: string
      ) => Promise<unknown>;
      const browserModule = await dynamicImport('@capacitor/browser').catch(() => null) as {
        Browser?: { open: (opts: { url: string; windowName?: string }) => Promise<void> };
      } | null;
      if (browserModule?.Browser) {
        await browserModule.Browser.open({ url, windowName: '_system' });
        return;
      }
    } catch {
      // Silently fall through to web behavior
    }
    // Fallback to web behavior if native browser not available
    window.open(url, '_blank', 'noopener,noreferrer');
  } else {
    // Web: open in new tab
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

/**
 * Trigger haptic feedback
 * - Web: uses navigator.vibrate (if available)
 * - Native: uses Capacitor Haptics plugin for better iOS integration
 */
export async function hapticImpact(style: 'light' | 'medium' | 'heavy' = 'light'): Promise<void> {
  if (isNativeApp()) {
    try {
      // Use Function constructor to prevent Vite from statically analyzing the import
      const dynamicImport = new Function('specifier', 'return import(specifier)') as (
        specifier: string
      ) => Promise<unknown>;
      const hapticsModule = await dynamicImport('@capacitor/haptics').catch(() => null) as {
        Haptics?: { impact: (opts: { style: unknown }) => Promise<void> };
        ImpactStyle?: { Light: unknown; Medium: unknown; Heavy: unknown };
      } | null;
      if (hapticsModule?.Haptics && hapticsModule?.ImpactStyle) {
        const impactStyleMap: Record<'light' | 'medium' | 'heavy', unknown> = {
          light: hapticsModule.ImpactStyle.Light,
          medium: hapticsModule.ImpactStyle.Medium,
          heavy: hapticsModule.ImpactStyle.Heavy,
        };
        await hapticsModule.Haptics.impact({ style: impactStyleMap[style] });
        return;
      }
    } catch {
      // Silently fall through to web behavior
    }
    // Fallback to web behavior if native haptics not available
    if ('vibrate' in navigator) {
      try {
        const durations: Record<'light' | 'medium' | 'heavy', number> = {
          light: 5,
          medium: 10,
          heavy: 15,
        };
        navigator.vibrate(durations[style]);
      } catch {
        // Ignore: Chrome may block vibrate until user gesture (intervention)
      }
    }
  } else {
    // Web: use navigator.vibrate (Chrome blocks until user has interacted with frame)
    if ('vibrate' in navigator) {
      try {
        const durations: Record<'light' | 'medium' | 'heavy', number> = {
          light: 5,
          medium: 10,
          heavy: 15,
        };
        navigator.vibrate(durations[style]);
      } catch {
        // Ignore: blocked by browser or not allowed before user gesture
      }
    }
  }
}

/**
 * Get runtime environment information
 * Returns mode: 'web' | 'pwa' | 'native'
 */
export function getRuntimeEnv(): { mode: 'web' | 'pwa' | 'native'; displayMode?: string } {
  if (isNativeApp()) {
    return { mode: 'native' };
  }

  // Check for PWA (standalone mode)
  // iOS Safari: navigator.standalone
  // Standard: matchMedia('(display-mode: standalone)')
  const isStandalone =
    ('standalone' in navigator && (navigator as Navigator & { standalone?: boolean }).standalone === true) ||
    window.matchMedia('(display-mode: standalone)').matches;

  if (isStandalone) {
    return { mode: 'pwa', displayMode: 'standalone' };
  }

  return { mode: 'web' };
}

/**
 * Check if the app might be running in iOS Simulator
 * This is a heuristic based on device characteristics and can help
 * provide better error messages for simulator-specific issues.
 *
 * Note: There's no 100% reliable way to detect simulator from JavaScript,
 * but we can check for common simulator characteristics.
 */
export function isLikelyIOSSimulator(): boolean {
  if (typeof window === 'undefined') return false;
  if (!isNativeApp()) return false;

  // Check for iOS platform
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  if (!isIOS) return false;

  // In Xcode simulators, window.screen values and devicePixelRatio can be checked
  // but the most reliable indicator in dev builds is checking for DEV mode
  // Real devices in production won't have DEV mode enabled
  const isDev = import.meta.env.DEV;

  // If we're in dev mode on iOS, it's very likely a simulator
  // (or a device connected to Xcode for debugging)
  return isDev;
}

/**
 * Get a user-friendly hint for iOS Simulator users
 * when Apple Sign-In fails
 */
export function getIOSSimulatorAppleSignInHint(): string | null {
  if (!isLikelyIOSSimulator()) return null;
  return 'If using iOS Simulator, ensure you are signed into an Apple ID in Settings → Apple ID.';
}

/**
 * Share content using native share sheet
 * - iOS Capacitor: Uses Web Share API (supported in WKWebView)
 * - Web: Uses Web Share API with fallback to clipboard
 *
 * Returns true if shared successfully, false if fallback was used
 */
export async function nativeShare(data: {
  title?: string;
  text?: string;
  url?: string;
}): Promise<{ shared: boolean; method: 'native' | 'clipboard' | 'none' }> {
  // Build share data - some iOS versions are picky about empty fields
  const shareData: ShareData = {};
  if (data.title) shareData.title = data.title;
  if (data.text) shareData.text = data.text;
  if (data.url) shareData.url = data.url;

  // Check if Web Share API is available and supported
  // Note: Some iOS versions have navigator.share but don't support canShare
  const hasShareApi = typeof navigator !== 'undefined' &&
    typeof navigator.share === 'function';

  // Try canShare if available, otherwise just check for share function
  const canShare = hasShareApi && (
    typeof navigator.canShare !== 'function' || navigator.canShare(shareData)
  );

  if (canShare) {
    try {
      await navigator.share(shareData);
      return { shared: true, method: 'native' };
    } catch (err) {
      const error = err as Error;
      // AbortError means user cancelled - that's okay
      if (error.name === 'AbortError') {
        return { shared: false, method: 'none' };
      }
      // NotAllowedError can occur if called outside user gesture (iOS)
      if (error.name === 'NotAllowedError') {
        console.warn('Share API not allowed - may need user gesture:', error.message);
      }
      // DataError occurs when data is invalid
      if (error.name === 'DataError') {
        console.warn('Share API data error:', error.message);
      }
      // Fall through to clipboard fallback
      console.warn('Share API failed, falling back to clipboard:', error.name);
    }
  }

  // Fallback: copy URL (or text if no URL) to clipboard
  const textToCopy = data.url || data.text;
  if (textToCopy) {
    // Try modern Clipboard API first
    if (typeof navigator?.clipboard?.writeText === 'function') {
      try {
        await navigator.clipboard.writeText(textToCopy);
        return { shared: true, method: 'clipboard' };
      } catch {
        // Clipboard API failed, try legacy fallback
      }
    }

    // Legacy fallback for older iOS versions using execCommand
    // This works in more contexts where Clipboard API might fail
    try {
      const textArea = document.createElement('textarea');
      textArea.value = textToCopy;
      // Prevent scrolling on iOS
      textArea.style.cssText = 'position:fixed;left:-9999px;top:0;opacity:0;';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      // Use selection range for iOS compatibility
      textArea.setSelectionRange(0, textToCopy.length);
      const success = document.execCommand('copy');
      document.body.removeChild(textArea);
      if (success) {
        return { shared: true, method: 'clipboard' };
      }
    } catch {
      // Legacy clipboard also failed
    }
  }

  return { shared: false, method: 'none' };
}
